import { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';

import defaultFilters from 'features/Statistics/defaultValues';

import { MultipleSelect } from 'components/FormControl';
import { TOAST } from 'constant';
import { toast } from 'layout';

function SearchFilters({
  visible,
  setVisible,
  filters,
  setFilters,
  appliedfilters,
  setAppliedFilters,
  projectTypes,
  programNames,
  programYears,
}) {
  // #region Data
  const location = useLocation();
  const { t } = useTranslation();

  const { control, handleSubmit, reset, trigger } = useForm({
    mode: 'onChange',
    defaultValues: {},
    shouldFocusError: true,
  });
  const { errors, isDirty, dirtyFields } = useFormState({
    control,
  });
  // #endregion Data

  // #region Event
  useEffect(() => {
    reset(filters);
    reset(appliedfilters);
  }, [filters, appliedfilters]);

  const onSubmit = (data) => {
    localStorage.setItem(`admin-${location.pathname.slice(1)}-filters`, JSON.stringify(data));

    setFilters(data);
    setAppliedFilters(data);
    setVisible(false);
  };

  const onClear = () => {
    reset({});
    localStorage.setItem(
      `admin-${location.pathname.slice(1)}-filters`,
      JSON.stringify(defaultFilters)
    );
    setVisible(false);
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion Event

  // #region Render
  return (
    <Dialog
      header={t('projectProposal.filter')}
      visible={visible}
      position="center"
      // style={{ width: '90%', marginInline: '1rem' }}
      className="w-6 md:w-6 lg:w-6 mx-1rem"
      onHide={() => setVisible(false)}
      draggable={false}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        className="p-fluid"
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={checkKeyDown}
        autoComplete="off"
      >
        <div className="grid">
          {/* programYears */}
          <div className="col-12 md:col-6 lg:col-6">
            <MultipleSelect
              label={t('projectProposal.year')}
              name="programYears"
              options={programYears}
              inputField="year"
              renderField="year"
              control={control}
              errors={errors}
            />
          </div>
          {/* projectType */}
          <div className="col-12 md:col-6 lg:col-6">
            <MultipleSelect
              label={t('projectProposal.viewList.projectType')}
              name="projectTypes"
              options={projectTypes}
              inputField="value"
              renderField="name"
              control={control}
              errors={errors}
            />
          </div>
          {/* programNames */}
          <div className="col-12 md:col-6 lg:col-6">
            <MultipleSelect
              label={t('projectProposal.viewList.program')}
              name="programNames"
              options={programNames}
              inputField="name"
              renderField="name"
              control={control}
              errors={errors}
            />
          </div>
        </div>
        <div className="flex justify-content-between">
          <Button
            label={t('projectProposal.viewList.clearFilter')}
            type="button"
            className="w-8rem"
            severity="danger"
            onClick={onClear}
          />

          <div className="flex justify-content-end gap-2">
            <Button
              label={t('formLayout.action.close')}
              type="button"
              className="w-7rem"
              severity="danger"
              onClick={() => setVisible(false)}
            />
            <Button
              label={t('employee.viewList.apply')}
              type="submit"
              className="w-7rem"
              severity="info"
              onClick={checkFormValid}
              disabled={!(isDirty && Object.keys(dirtyFields).length)}
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

export default SearchFilters;

SearchFilters.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  appliedfilters: PropTypes.bool.isRequired,
  setAppliedFilters: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  programYears: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  projectTypes: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  programNames: PropTypes.array,
};

SearchFilters.defaultProps = {
  programYears: [],
  projectTypes: [],
  programNames: [],
};
